import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { toSignal } from '@angular/core/rxjs-interop';

import { InlineSvgDirective } from '@directives/inline-svg';
import {
  InvestorBulkEmailRepository,
  InvestorBulkEmailSend,
  InvestorBulkEmailService
} from '@state/investor-bulk-email';

interface ProgressRow extends InvestorBulkEmailSend {
  failedCount: string;
  percent: number;
  retryingCount: string;
  sentCount: string;
  title: string;
}

// No message counts while things are going well: one investor can yield
// several personalizations (team members, unconfirmed primaries), so a number
// would not match what the user expects from the investor list. Once something
// fails, the sent/failed split is what the user needs to act on, so it's shown.
function title({ complete, failed }: InvestorBulkEmailSend): string {
  if (!complete) return 'Sending messages';
  return failed.length ? 'Some messages failed' : 'All messages sent successfully';
}

function toProgressRow(send: InvestorBulkEmailSend): ProgressRow {
  let reported = send.sent + send.failed.length;
  let percent = send.total ? Math.min(100, Math.round((reported / send.total) * 100)) : 0;

  return {
    ...send,
    // plain digits (no thousands separator) so four digits fill exactly 4ch
    failedCount: String(send.failed.length),
    percent,
    retryingCount: String(send.retrying),
    sentCount: String(send.sent),
    title: title(send)
  };
}

// Floating card (bottom-right, survives navigation) showing each in-flight bulk
// investor send. Rendered once in the app shell.
@Component({
  encapsulation: ViewEncapsulation.None,
  imports: [InlineSvgDirective, MatProgressBar],
  selector: 'fl-investor-bulk-email-progress',
  styleUrls: ['./investor-bulk-email-progress.component.scss'],
  templateUrl: './investor-bulk-email-progress.component.html'
})
export class InvestorBulkEmailProgressComponent {
  private clipboard = inject(Clipboard);
  private investorBulkEmailRepository = inject(InvestorBulkEmailRepository);
  private investorBulkEmailService = inject(InvestorBulkEmailService);
  private snackBar = inject(MatSnackBar);

  private visibleSends = toSignal(this.investorBulkEmailRepository.visibleSends$, { initialValue: [] });

  rows = computed(() => this.visibleSends().map(toProgressRow));

  copyFailedEmails({ failed }: ProgressRow): void {
    this.clipboard.copy(failed.map(({ email }) => email).join('\n'));
    this.snackBar.open(`Copied ${failed.length === 1 ? '1 email' : `${failed.length.toLocaleString()} emails`} to clipboard`);
  }

  dismiss(channel: string): void {
    this.investorBulkEmailService.dismiss(channel);
  }
}
